import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Cog,
  Contact,
  Mail,
  Send,
  Users,
  ShieldCheck,
  Database,
  MapPin,
  ScrollText,
  Workflow,
  FlaskConical,
  Gauge,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useAccess } from './use-access';
import { isNavItemEnabled, isNavItemHidden, visibleSortedNavItems } from './nav-items';
import { SignInGem } from './SignInGem';
import { ocrEvalConfigApi } from '../modules/ocr-eval/api';
import { encounterBenchmarkConfigApi } from '../modules/encounter-benchmark/api';

// Class composition for nav link rows. `collapsed` only affects md+ — on
// mobile the drawer is always full-width, so labels stay visible.
function buildNavLinkClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }) =>
    `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
      isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
    } ${collapsed ? 'md:justify-center md:px-2' : ''}`;
}

// Icon-only mode: text label in main row hides on md+, tooltip springs in
// on hover. Both are no-ops on mobile (collapsed has no effect there).
function labelClass(collapsed: boolean) {
  return collapsed ? 'md:hidden' : '';
}

// Tooltip rendered on every collapsed icon. Hidden on mobile (we have full
// labels there); on md+, becomes visible on parent `group` hover.
function CollapsedTooltip({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (!collapsed) return null;
  return (
    <span
      role="tooltip"
      className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 shadow-lg hidden md:group-hover:inline-block md:group-focus-visible:inline-block"
    >
      {label}
    </span>
  );
}

/** `adminOnly`: Ruby admins and VELOX support ("staff") — support sets clinics up. */
const SETTINGS_LINKS: { label: string; icon: LucideIcon; path: string; adminOnly?: boolean }[] = [
  // First so it sits directly under Workflow Map — the two entries a non-admin
  // can see stay adjacent in the admin view too. Not admin-only: clinic staff
  // curate the book their document emails are sent to.
  { label: 'Email Address Book', icon: Contact, path: '/address-book' },
  // Directly under the book, and not admin-only for the same reason: the people
  // who curate who a document goes to also curate what it says.
  { label: 'Email Templates', icon: Mail, path: '/email-templates' },
  // Third in the email cluster. An operational log rather than a setting, so it
  // sits here for discoverability — staff look for every email surface in one
  // place — not because it configures anything.
  { label: 'Emailed Documents', icon: Send, path: '/emailed-documents' },
  { label: 'Clinic Configuration', icon: Cog, path: '/admin/clinic-config', adminOnly: true },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  /** Desktop-only: collapse to icon-only mode (md+). Ignored on mobile —
   *  the drawer always renders full-width when open. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function Sidebar({ open, onClose, collapsed, onToggleCollapsed }: SidebarProps) {
  const { user, clinics, selectedClinic } = useAuth();
  const canViewWorkflowMap =
    user?.role === 'admin' || (user?.permissions ?? []).includes('view_workflow_map');
  // Staff Workload Dashboard sub-link visibility — admin always sees it;
  // clinic users need the explicit subrole permission. Plan:
  // 2026-05-10-staff-workload-dashboard.md.
  const canViewStaffWorkload =
    user?.role === 'admin' || (user?.permissions ?? []).includes('staff.view_workload');
  // Documents → Referring physicians sub-link visibility — admin always,
  // clinic users need referring_doctors.view (plan 2026-05-30, Phase 6).
  const canViewReferringDoctors =
    user?.role === 'admin' || (user?.permissions ?? []).includes('referring_doctors.view');
  const currentClinic = clinics.find((c) => c.clientId === selectedClinic);
  const enabledModules = currentClinic?.enabledModules ?? [];
  // Ruby admin vs staff (admin or VELOX support). Staff see the admin view of
  // the menu — inactive modules, the setup pages; Ruby's own tooling and
  // pipeline editors stay Ruby-only. Plan 2026-09-25-ruby-hq-support-role.md §7.
  const { isRubyAdmin: isAdmin, isStaff, level, isUserManager } = useAccess();

  // OCR Evals — staging-only, admin-only requisition-OCR tuning tool. The
  // server's GET /api/ocr-eval-config returns {enabled:true} only for admins on
  // staging, so the nav entry shows only when both the role check AND the flag
  // pass. Cached aggressively (the flag never changes within a session).
  const ocrEvalConfigQ = useQuery({
    queryKey: ['ocr-eval', 'config'],
    queryFn: () => ocrEvalConfigApi.get(),
    enabled: isAdmin,
    staleTime: Infinity,
    retry: false,
  });
  const showOcrEval = isAdmin && ocrEvalConfigQ.data?.enabled === true;

  // Encounter Benchmark — staging-only, admin-only (GET /api/encounter-benchmark-config).
  const encBenchmarkConfigQ = useQuery({
    queryKey: ['encounter-benchmark', 'config'],
    queryFn: () => encounterBenchmarkConfigApi.get(),
    enabled: isAdmin,
    staleTime: Infinity,
    retry: false,
  });
  const showEncBenchmark = isAdmin && encBenchmarkConfigQ.data?.enabled === true;

  // Per-client menu curation (migration 0143). Admins keep every entry and get a
  // `Hidden` badge instead, so they can see what the clinic sees — and so the
  // admin-driven e2e sidebar drift guard is unaffected by any clinic's config.
  // Filter-then-rank ordering is pinned by tests inside the helper.
  // Plan: docs/plans/2026-08-17-per-client-nav-and-landing.md
  const navSettings = { navHidden: currentClinic?.navHidden };
  // The Calendar carries NO permission filter (changed 2026-08-22) — it is
  // always-on in the menu for every role, and a clinic that doesn't want the
  // entry hides it via `nav_hidden` like any other. See `calendar-api.ts`.
  // A subrole confined to some pages (Front Desk) sees only those, and no
  // settings block below — each entry there is a page of its own it cannot open.
  const pages = user?.pages;
  const sortedModules = visibleSortedNavItems(navSettings, enabledModules, isStaff, pages);

  const navCls = buildNavLinkClass(collapsed);
  const labelCls = labelClass(collapsed);

  return (
    <aside className={`
      w-60 ${collapsed ? 'md:w-16' : 'md:w-60'} h-screen bg-white border-r border-gray-200 flex flex-col shrink-0
      fixed z-50 transition-all duration-200 ease-in-out
      md:static md:translate-x-0
      ${open ? 'translate-x-0' : '-translate-x-full'}
    `}>
      {/* Logo + mobile close + desktop collapse toggle */}
      <div className={`flex items-center gap-3 ${collapsed ? 'p-3 md:flex-col md:gap-2' : 'p-5'}`}>
        <SignInGem />
        <span className={`text-lg font-bold text-gray-900 ${labelCls}`}>Ruby HQ</span>
        <button onClick={onClose} className="ml-auto p-1 text-gray-400 hover:text-gray-600 md:hidden">
          <X size={20} />
        </button>
        {/* Desktop collapse toggle. Always visible on md+, never on mobile.
         *  When expanded, sits to the right of the title via ml-auto;
         *  when collapsed, the parent flips to flex-col so it stacks
         *  centered below the gem. */}
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`hidden md:flex items-center justify-center p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors ${
            collapsed ? '' : 'ml-auto'
          }`}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      {/* Single scrollable block: nav + settings + footer scroll together.
       *  Only the logo/header row above stays pinned. */}
      <div className="flex-1 overflow-y-auto flex flex-col">
      {/* Module links */}
      <nav className="px-3 py-2 space-y-0.5">
        {sortedModules.map((mod) => {
          const Icon = mod.icon;

          // Only ever true for an admin — a client's hidden items are dropped by
          // `visibleSortedNavItems` before any of these branches, so a hidden
          // item can never come back as a greyed "Upgrade" or "Soon" row.
          const isHidden = isNavItemHidden(mod, navSettings);

          // Coming Soon — always non-clickable, at the bottom
          if (mod.comingSoon) {
            return (
              <div
                key={mod.id}
                className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 cursor-not-allowed ${
                  collapsed ? 'md:justify-center md:px-2' : ''
                }`}
              >
                <Icon size={18} />
                <span className={`text-sm ${labelCls}`}>{mod.label}</span>
                <span className={`ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded ${labelCls} ${
                  isHidden ? 'bg-slate-100 text-slate-500' : 'bg-gray-100 text-gray-400'
                }`}>
                  {isHidden ? 'Hidden' : 'Soon'}
                </span>
                <CollapsedTooltip
                  label={isHidden ? `${mod.label} — Hidden from this clinic` : `${mod.label} — Coming soon`}
                  collapsed={collapsed}
                />
              </div>
            );
          }

          const isEnabled = isNavItemEnabled(mod, enabledModules);

          // Inactive module — non-admin sees "Upgrade" badge
          if (!isEnabled && !isStaff) {
            return (
              <div
                key={mod.id}
                className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 cursor-not-allowed ${
                  collapsed ? 'md:justify-center md:px-2' : ''
                }`}
              >
                <Icon size={18} />
                <span className={`text-sm ${labelCls}`}>{mod.label}</span>
                <span className={`ml-auto text-[10px] font-medium bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded ${labelCls}`}>
                  Upgrade
                </span>
                <CollapsedTooltip label={`${mod.label} — Upgrade required`} collapsed={collapsed} />
              </div>
            );
          }

          return (
            <div key={mod.id} className="relative">
              <NavLink to={mod.path} className={navCls} onClick={onClose}>
                <Icon size={18} />
                <span className={labelCls}>{mod.label}</span>
                <CollapsedTooltip
                  label={
                    isHidden
                      ? `${mod.label} (Hidden from this clinic)`
                      : !isEnabled && isStaff
                        ? `${mod.label} (Inactive)`
                        : mod.label
                  }
                  collapsed={collapsed}
                />
              </NavLink>
              {/* Admin-only state badges — absolute-positioned siblings of the
               *  NavLink so the anchor's textContent stays equal to the label
               *  (decorative-only markers, not part of the link's accessible
               *  name). `pointer-events-none` keeps the whole row clickable.
               *  One slot: "Hidden" wins over "Inactive" because it's the one an
               *  admin can act on from Clinic Configuration, and it's the answer
               *  to "where did my page go?". */}
              {isStaff && (isHidden || !isEnabled) && (
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium px-1.5 py-0.5 rounded ${labelCls} ${
                    isHidden ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-600'
                  }`}
                >
                  {isHidden ? 'Hidden' : 'Inactive'}
                </span>
              )}
              {/* Documents → Workload (second-level). Visible only when
                  Documents is enabled AND the user has staff.view_workload.
                  Indented in expanded mode so the nesting reads visually;
                  hides entirely in icon-collapsed mode (no room for the
                  sub-label, and the parent's tooltip already names it). */}
              {mod.id === 'document-manager' && isEnabled && canViewStaffWorkload && !collapsed && (
                <NavLink
                  to="/documents/staff"
                  className={({ isActive }) =>
                    `flex items-center gap-2 pl-10 pr-3 py-1.5 rounded-lg text-xs transition-colors ${
                      isActive ? 'text-indigo-700 font-medium bg-indigo-50' : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                  onClick={onClose}
                >
                  <span>Workload</span>
                </NavLink>
              )}
              {mod.id === 'document-manager' && isEnabled && canViewReferringDoctors && !collapsed && (
                <NavLink
                  to="/documents/referring-doctors"
                  className={({ isActive }) =>
                    `flex items-center gap-2 pl-10 pr-3 py-1.5 rounded-lg text-xs transition-colors ${
                      isActive ? 'text-indigo-700 font-medium bg-indigo-50' : 'text-gray-600 hover:bg-gray-50'
                    }`
                  }
                  onClick={onClose}
                >
                  <span>Referring physicians</span>
                </NavLink>
              )}
            </div>
          );
        })}
      </nav>

      {/* Settings section — none of it opens for a user confined to some pages. */}
      {!pages && (<div className="px-3 pb-2">
        <div className="border-t border-gray-200 pt-3 space-y-0.5">
          {/* VELOX L2 and up, and a clinic user manager (plan 2026-10-22-clinic-user-managers). */}
          {(level >= 2 || isUserManager) && (
            <NavLink to="/admin/users" className={navCls} onClick={onClose}>
              <Users size={18} />
              <span className={labelCls}>User Management</span>
              <CollapsedTooltip label="User Management" collapsed={collapsed} />
            </NavLink>
          )}
          {isStaff && (<>
            <NavLink to="/admin/procedures" className={navCls} onClick={onClose}>
              <Database size={18} />
              <span className={labelCls}>Procedures</span>
              <CollapsedTooltip label="Procedures" collapsed={collapsed} />
            </NavLink>
            <NavLink to="/admin/locations" className={navCls} onClick={onClose}>
              <MapPin size={18} />
              <span className={labelCls}>Locations</span>
              <CollapsedTooltip label="Locations" collapsed={collapsed} />
            </NavLink>
          </>)}
          {isAdmin && (<>
            <NavLink to="/admin/subroles" className={navCls} onClick={onClose}>
              <ShieldCheck size={18} />
              <span className={labelCls}>Subroles</span>
              <CollapsedTooltip label="Subroles" collapsed={collapsed} />
            </NavLink>
            <NavLink to="/admin/workflows" className={navCls} onClick={onClose}>
              <Workflow size={18} />
              <span className={labelCls}>Document Workflows</span>
              <CollapsedTooltip label="Document Workflows" collapsed={collapsed} />
            </NavLink>
            <NavLink to="/admin/workflow-diagram" className={navCls} onClick={onClose}>
              <LayoutDashboard size={18} />
              <span className={labelCls}>Workflow Diagram</span>
              <CollapsedTooltip label="Workflow Diagram" collapsed={collapsed} />
            </NavLink>
            <NavLink to="/admin/audit" className={navCls} onClick={onClose}>
              <ScrollText size={18} />
              <span className={labelCls}>Audit Log</span>
              <CollapsedTooltip label="Audit Log" collapsed={collapsed} />
            </NavLink>
            {/* OCR Evals — staging-only requisition-OCR tuning tool. Renders
                only when the server flag (admin + staging) is on. */}
            {showOcrEval && (
              <NavLink to="/ocr-eval" className={navCls} onClick={onClose}>
                <FlaskConical size={18} />
                <span className={labelCls}>OCR Evals</span>
                <CollapsedTooltip label="OCR Evals" collapsed={collapsed} />
              </NavLink>
            )}
            {/* Encounter Benchmark — staging-only; Ruby-vs-human procedure benchmark. */}
            {showEncBenchmark && (
              <NavLink to="/encounter-benchmark" className={navCls} onClick={onClose}>
                <Gauge size={18} />
                <span className={labelCls}>Encounter Benchmark</span>
                <CollapsedTooltip label="Encounter Benchmark" collapsed={collapsed} />
              </NavLink>
            )}
          </>)}
          {/* Workflow Map — visible to anyone with view_workflow_map (admins
              always have it; non-admin clinical managers gain it via subrole).
              Lives outside the isAdmin block so non-admins with the permission
              can navigate to the page they were granted access to. */}
          {canViewWorkflowMap && (
            <NavLink to="/workflow-map" className={navCls} onClick={onClose}>
              <Workflow size={18} />
              <span className={labelCls}>Workflow Map</span>
              <CollapsedTooltip label="Workflow Map" collapsed={collapsed} />
            </NavLink>
          )}
          {SETTINGS_LINKS.map((link) => {
            const Icon = link.icon;
            // Hide staff-only links from clinic users
            if (link.adminOnly && !isStaff) return null;
            return (
              <NavLink key={link.label} to={link.path} className={navCls} onClick={onClose}>
                <Icon size={18} />
                <span className={labelCls}>{link.label}</span>
                <CollapsedTooltip label={link.label} collapsed={collapsed} />
              </NavLink>
            );
          })}
        </div>
      </div>)}

      {/* Footer — hidden in collapsed mode (no horizontal room for the
       *  copyright/version lines). `mt-auto` keeps it at the bottom of the
       *  scrollable column when the content is shorter than the viewport;
       *  scrolls naturally with the rest when it overflows. */}
      <div className={`mt-auto px-5 py-3 border-t border-gray-100 text-[11px] text-gray-400 ${labelCls}`}>
        <div>&copy; 2026 Ruby Health</div>
        <div>Version 0.1.0</div>
      </div>
      </div>
    </aside>
  );
}
